import type { QueryClient, QueryObserverOptions } from '@tanstack/query-core';
import type { ResultDetail } from '../../shared/api';
import { QueryHttpError } from '../api/http';
import { readArchivedResult } from '../api/results';

const ARCHIVE_GC_TIME = 10 * 60_000;

type ArchiveKey = readonly ['archive', number, string];

export function archiveQueryKey(profileGeneration: number, roundId: string): ArchiveKey {
  return ['archive', profileGeneration, roundId];
}

export function archiveQueryOptions(
  roundId: string,
  credential: string | null,
  profileGeneration: number,
  enabled = true,
): QueryObserverOptions<ResultDetail, QueryHttpError, ResultDetail, ResultDetail, ArchiveKey> {
  const requestCredential = credential;
  return {
    queryKey: archiveQueryKey(profileGeneration, roundId),
    queryFn: ({ signal }) => {
      if (!requestCredential) throw new QueryHttpError('Result unavailable for this profile.', 401);
      return readArchivedResult(roundId, requestCredential, signal);
    },
    enabled: enabled && Boolean(requestCredential),
    staleTime: Infinity,
    gcTime: ARCHIVE_GC_TIME,
  };
}

function isPrivateQuery(query: { queryKey: readonly unknown[] }): boolean {
  return query.queryKey[0] === 'archive' || query.queryKey[0] === 'my-rooms';
}

export async function removePrivateGeneration(client: QueryClient, generation: number): Promise<void> {
  const filters = {
    predicate: (query: { queryKey: readonly unknown[] }) =>
      isPrivateQuery(query) && query.queryKey[1] === generation,
  };
  await client.cancelQueries(filters);
  client.removeQueries(filters);
}

function invalidateArchivedResultLists(client: QueryClient): void {
  void client.invalidateQueries({ queryKey: ['leaderboard'] });
  // A ranked archive moves the player's rank and the ladder.
  void client.invalidateQueries({ queryKey: ['ranked-profile'] });
  void client.invalidateQueries({ queryKey: ['ladder'] });
}

/** Bridge archive-ready lifecycle observations to list invalidation once per round. */
export function createArchiveListInvalidator(client: QueryClient): (roundId: string | null) => void {
  const seen = new Set<string>();
  return (roundId) => {
    if (!roundId || seen.has(roundId)) return;
    seen.add(roundId);
    invalidateArchivedResultLists(client);
  };
}
